import type { StepMerged, StepTarget } from 'react-joyride';
import type { ITourStepData } from '@srm/core/contracts/tours';

export interface ITourRect {
    left: number;
    top: number;
    width: number;
    height: number;
    radius: number;
}

export function resolveTourTarget(target: StepTarget | undefined): HTMLElement | null {
    try {
        if (typeof target === 'string') return document.querySelector<HTMLElement>(target);
        if (typeof target === 'function') return target();
        if (target && 'current' in target) return target.current;
        return target ?? null;
    } catch {
        return null;
    }
}

export function measureTourSpotlight(step: StepMerged): ITourRect | null {
    if (step.placement === 'center') return null;
    const data = step.data as ITourStepData | null;
    const element = resolveTourTarget(step.spotlightTarget ?? step.target);
    if (!element) return null;
    const rects = [element, ...(data?.spotlightUnion ?? []).map(resolveTourTarget)]
        .filter((el): el is HTMLElement => !!el)
        .map((el) => el.getBoundingClientRect())
        .filter((rect) => rect.width > 0 && rect.height > 0);
    if (!rects.length) return null;
    const padding = step.spotlightPadding;
    const left = Math.min(...rects.map((r) => r.left)) - padding.left;
    const top = Math.min(...rects.map((r) => r.top)) - padding.top;
    const right = Math.max(...rects.map((r) => r.right)) + padding.right;
    const bottom = Math.max(...rects.map((r) => r.bottom)) + padding.bottom;
    const radius = Math.max(8, Math.min(20, parseFloat(getComputedStyle(element).borderRadius) || 8));
    return { left, top, width: right - left, height: bottom - top, radius };
}

export function tourSpotlightPath(rect: ITourRect): string {
    const { left: x, top: y, width: w, height: h } = rect;
    const r = Math.min(rect.radius, w / 2, h / 2);
    return `M${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h - r}Q${x + w},${y + h} ${x + w - r},${y + h}H${x + r}Q${x},${y + h} ${x},${y + h - r}V${y + r}Q${x},${y} ${x + r},${y}Z`;
}
